import { useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalState } from '../state/useGlobalState';
import { useStore } from './useStore';
import { api } from '../services/api';
import { logger } from '../services/logger.client';
// Import resetCriticalBootstrapState directly from useCriticalBootstrap to avoid
// circular dependency through useBootstrapCoordinator (which imports useAuth).
import { resetCriticalBootstrapState } from './bootstrap/useCriticalBootstrap';

interface LoginResponse {
  user: { id: number; username: string; email: string; role: 'USER' | 'ADMIN'; };
  accessToken: string;
}

interface MessageResponse {
  message: string;
}

type AuthAsyncState = {
  status: 'idle' | 'loading' | 'success' | 'error';
  error: { message: string; [key: string]: unknown } | null;
};

type AuthAsyncAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS' }
  | { type: 'FETCH_ERROR'; payload: { message: string; [key: string]: unknown } }
  | { type: 'RESET' };

const initialAsyncState: AuthAsyncState = {
  status: 'idle',
  error: null,
};

const authAsyncReducer = (state: AuthAsyncState, action: AuthAsyncAction): AuthAsyncState => {
  switch (action.type) {
    case 'FETCH_START': return { ...initialAsyncState, status: 'loading' };
    case 'FETCH_SUCCESS': return { ...initialAsyncState, status: 'success' };
    case 'FETCH_ERROR': return { ...state, status: 'error', error: action.payload };
    case 'RESET': return initialAsyncState;
    default: return state;
  }
};

export const useAuth = () => {
  const user = useStore(state => state.auth?.user);
  const token = useStore(state => state.auth?.token);
  const { login: loginAction, logout: logoutAction } = useStore(state => state.auth);

  const navigate = useNavigate();
  const [state, dispatch] = useReducer(authAsyncReducer, initialAsyncState);
  const isHydrated = useGlobalState.persist.hasHydrated();

  const login = async (username: string, password: string): Promise<boolean> => {
    dispatch({ type: 'FETCH_START' });
    try {
      const response = await api('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json() as LoginResponse | null;

      // Validate that the response contains required fields
      if (!data?.user || !data.accessToken) {
        throw new Error('Login response was empty.');
      }

      logger.info({ user: data.user.username }, 'useAuth: Calling loginAction and navigating to root.');
      await loginAction(data.user, data.accessToken);
      logger.info({ user: data.user.username }, 'User logged in successfully.');
      dispatch({ type: 'FETCH_SUCCESS' });
      return true;
    } catch (err: unknown) {
      const errorPayload = {
        message: err instanceof Error ? err.message : 'An unknown error occurred during login.',
        ...(err instanceof Object ? err : {}),
      };
      dispatch({ type: 'FETCH_ERROR', payload: errorPayload });
      return false;
    }
  };

  const register = async (username: string, email: string, password: string): Promise<boolean> => {
    dispatch({ type: 'FETCH_START' });
    try {
      await api('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ username, email, password }),
      });
      dispatch({ type: 'FETCH_SUCCESS' });
      return true;
    } catch (err: unknown) {
      const errorPayload = {
        message: err instanceof Error ? err.message : 'An unknown error occurred during registration.',
        ...(err instanceof Object ? err : {}),
      };
      dispatch({ type: 'FETCH_ERROR', payload: errorPayload });
      return false;
    }
  };

  const logout = async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } catch (error) {
      logger.warn(error, 'useAuth: Logout API call failed, clearing state locally anyway.');
    } finally {
      logger.info('useAuth: Resetting bootstrap state and calling logoutAction.');
      // Reset module-level bootstrap state so next login triggers a fresh bootstrap fetch.
      // This must happen before logoutAction() to ensure clean state for the next session.
      resetCriticalBootstrapState();
      logoutAction();
      logger.info('User logged out.');
      navigate('/login');
    }
  };

  const forgotPassword = async (email: string): Promise<{ success: boolean, message: string }> => {
    dispatch({ type: 'FETCH_START' });
    try {
      const response = await api('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      const data = await response.json() as MessageResponse;
      dispatch({ type: 'FETCH_SUCCESS' });
      return { success: true, message: data?.message || 'Request sent successfully.' };
    } catch (err: unknown) {
      const errorPayload = {
        message: err instanceof Error ? err.message : 'An unknown error occurred.',
        ...(err instanceof Object ? err : {}),
      };
      dispatch({ type: 'FETCH_ERROR', payload: errorPayload });
      return { success: false, message: err instanceof Error ? err.message : 'An unknown error occurred.' };
    }
  };

  const resetPassword = async (token: string, password: string): Promise<{ success: boolean, message: string }> => {
    dispatch({ type: 'FETCH_START' });
    try {
      const response = await api('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json() as MessageResponse;
      dispatch({ type: 'FETCH_SUCCESS' });
      return { success: true, message: data?.message || 'Password has been reset.' };
    } catch (err: unknown) {
      const errorPayload = {
        message: err instanceof Error ? err.message : 'An unknown error occurred.',
        ...(err instanceof Object ? err : {}),
      };
      dispatch({ type: 'FETCH_ERROR', payload: errorPayload });
      return { success: false, message: err instanceof Error ? err.message : 'An unknown error occurred.' };
    }
  };

  const verifyEmail = async (token: string): Promise<{ success: boolean, message: string }> => {
    dispatch({ type: 'FETCH_START' });
    try {
      const response = await api(`/api/auth/verify-email/${token}`, { method: 'GET' });
      const data = await response.json() as MessageResponse;
      dispatch({ type: 'FETCH_SUCCESS' });
      return { success: true, message: data?.message || 'Email verified successfully.' };
    } catch (err: unknown) {
      const errorPayload = {
        message: err instanceof Error ? err.message : 'An unknown error occurred.',
        ...(err instanceof Object ? err : {}),
      };
      dispatch({ type: 'FETCH_ERROR', payload: errorPayload });
      return { success: false, message: err instanceof Error ? err.message : 'An unknown error occurred.' };
    }
  };

  const resendVerification = async (email: string): Promise<{ success: boolean, message: string }> => {
    dispatch({ type: 'FETCH_START' });
    try {
      const response = await api('/api/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      const data = await response.json() as MessageResponse;
      dispatch({ type: 'FETCH_SUCCESS' });
      return { success: true, message: data?.message || 'Verification email sent.' };
    } catch (err: unknown) {
      const errorPayload = {
        message: err instanceof Error ? err.message : 'An unknown error occurred.',
        ...(err instanceof Object ? err : {}),
      };
      dispatch({ type: 'FETCH_ERROR', payload: errorPayload });
      return { success: false, message: err instanceof Error ? err.message : 'An unknown error occurred.' };
    }
  };

  return {
    isAuthenticated: !!token,
    isAuthStateHydrated: isHydrated, // Expose hydration status with a more explicit name
    user,
    token,
    login,
    register,
    logout,    
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
    loading: state.status === 'loading',
    error: state.error,
  };
};